/**
 * SB2-W-01 — UI copy + role-set constants for the in-product role-
 * management surface. Sourced verbatim from Christian's OQ-SB2-A2(b)
 * answer (2026-05-20):
 *
 *   "Tenant-admin role is itself a privileged position; promoting to
 *    peer should require platform-admin authorisation. This matches
 *    ISO 27001 A.6.1.2 separation-of-duties. I would like to add a
 *    info icon to show the info: ..."
 *
 * Mirrored on the BE side as a comment header on the SoD predicate
 * (`services/api-gateway/src/clients/auth/role-predicates.ts ::
 * tenantAdminWithSoD`). Drift between the two strings is intentional
 * to catch as a code-review smell — the FE copy is the canonical
 * user-visible form; the BE comment is a developer rationale.
 */

import type { Role } from "../queries/types.js";

export const ISO_27001_TOOLTIP =
  "Tenant-admin role is itself a privileged position; promoting to peer " +
  "should require platform-admin authorisation. This matches ISO 27001 " +
  "A.6.1.2 separation-of-duties.";

export const ISO_27001_TOOLTIP_TITLE =
  "ISO 27001 A.6.1.2 — Separation of duties";

/**
 * Roles a `tenant_admin` caller is permitted to grant via the
 * `PATCH /api/auth/v1/admin/users/{userId}/role` endpoint. The BE
 * matrix cell + `tenantAdminWithSoD` predicate enforce this set
 * server-side; the FE filter is a usability aid, not a security
 * control.
 */
export const TENANT_ADMIN_ASSIGNABLE_ROLES: readonly Role[] = [
  "project_manager",
  "expert_analyst",
  "pre_screener",
  "pilot",
  "back_office",
] as const;

/**
 * Roles a `platform_admin` caller may grant cross-tenant via
 * `PATCH /api/auth/v1/platform-admin/users/{userId}/role`. All seven
 * canonical roles.
 */
export const PLATFORM_ADMIN_ASSIGNABLE_ROLES: readonly Role[] = [
  "platform_admin",
  "tenant_admin",
  "project_manager",
  "expert_analyst",
  "pre_screener",
  "pilot",
  "back_office",
] as const;
